import dayjs from 'dayjs';

type CallIntelTrialDateRange = {
  startDate?: string;
  endDate?: string;
};

type CallIntelTrialDateRangeMap = Record<string, CallIntelTrialDateRange>;

type GetCallIntelTrialDetailsParams = {
  locationIds: string[];
  trialDateRangeMap: CallIntelTrialDateRangeMap;
};

type CallIntelTrialDetails = {
  aggregatedTrialDaysRemaining: number;
  locationWiseTrialDaysRemaining: Record<string, number>;
  minimumTrialStartDate: Date | null;
  maxTrialEndDate: Date | null;
  hasVaryingDaysRemaining: boolean;
};

export const getCallIntelTrialDetails = ({
  locationIds,
  trialDateRangeMap,
}: GetCallIntelTrialDetailsParams): CallIntelTrialDetails => {
  const now = dayjs();
  const locationWiseTrialDaysRemaining: Record<string, number> = {};
  let minimumTrialStartDate: Date | null = null;
  let maxTrialEndDate: Date | null = null;
  let minimumTrialDaysRemaining: number | null = null;

  locationIds.forEach((locationId) => {
    const trialDateRange = trialDateRangeMap[locationId];
    const startDate = trialDateRange?.startDate;
    const endDate = trialDateRange?.endDate;

    if (startDate) {
      const parsedStartDate = new Date(startDate);
      if (!minimumTrialStartDate || parsedStartDate < minimumTrialStartDate) {
        minimumTrialStartDate = parsedStartDate;
      }
    }

    if (endDate) {
      const parsedEndDate = new Date(endDate);
      if (!maxTrialEndDate || parsedEndDate > maxTrialEndDate) {
        maxTrialEndDate = parsedEndDate;
      }

      const trialDaysRemaining = Math.max(0, dayjs(endDate).diff(now, 'day') + 1);
      locationWiseTrialDaysRemaining[locationId] = trialDaysRemaining;

      minimumTrialDaysRemaining =
        minimumTrialDaysRemaining === null
          ? trialDaysRemaining
          : Math.min(minimumTrialDaysRemaining, trialDaysRemaining);
      return;
    }

    locationWiseTrialDaysRemaining[locationId] = 0;
    minimumTrialDaysRemaining = minimumTrialDaysRemaining === null ? 0 : Math.min(minimumTrialDaysRemaining, 0);
  });

  const trialDaysRemainingValues = Object.values(locationWiseTrialDaysRemaining);
  const hasVaryingDaysRemaining = trialDaysRemainingValues.length > 1 && new Set(trialDaysRemainingValues).size > 1;

  return {
    aggregatedTrialDaysRemaining: locationIds.length > 0 ? minimumTrialDaysRemaining ?? 0 : 0,
    locationWiseTrialDaysRemaining,
    minimumTrialStartDate,
    maxTrialEndDate,
    hasVaryingDaysRemaining,
  };
};
